import { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import axios from 'axios';
import { getAuthConfig } from '../utils/auth';
import Navbar from '../components/Navbar';
import SquarePaymentForm from '../components/SquarePaymentForm';
import { Button } from '../components/ui/button';
import { Check, CreditCard, Lock, ArrowLeft, MapPin, Truck, Package, Info, Building2, Mail, AlertTriangle, Clock, ShieldCheck, Send } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const SERVICE_FEE_PERCENTAGE = 0.03; // 3%

// US State options for dropdown
const US_STATES = [
  { code: 'AL', name: 'Alabama' }, { code: 'AK', name: 'Alaska' }, { code: 'AZ', name: 'Arizona' },
  { code: 'AR', name: 'Arkansas' }, { code: 'CA', name: 'California' }, { code: 'CO', name: 'Colorado' },
  { code: 'CT', name: 'Connecticut' }, { code: 'DE', name: 'Delaware' }, { code: 'FL', name: 'Florida' },
  { code: 'GA', name: 'Georgia' }, { code: 'HI', name: 'Hawaii' }, { code: 'ID', name: 'Idaho' },
  { code: 'IL', name: 'Illinois' }, { code: 'IN', name: 'Indiana' }, { code: 'IA', name: 'Iowa' },
  { code: 'KS', name: 'Kansas' }, { code: 'KY', name: 'Kentucky' }, { code: 'LA', name: 'Louisiana' },
  { code: 'ME', name: 'Maine' }, { code: 'MD', name: 'Maryland' }, { code: 'MA', name: 'Massachusetts' },
  { code: 'MI', name: 'Michigan' }, { code: 'MN', name: 'Minnesota' }, { code: 'MS', name: 'Mississippi' },
  { code: 'MO', name: 'Missouri' }, { code: 'MT', name: 'Montana' }, { code: 'NE', name: 'Nebraska' },
  { code: 'NV', name: 'Nevada' }, { code: 'NH', name: 'New Hampshire' }, { code: 'NJ', name: 'New Jersey' },
  { code: 'NM', name: 'New Mexico' }, { code: 'NY', name: 'New York' }, { code: 'NC', name: 'North Carolina' },
  { code: 'ND', name: 'North Dakota' }, { code: 'OH', name: 'Ohio' }, { code: 'OK', name: 'Oklahoma' },
  { code: 'OR', name: 'Oregon' }, { code: 'PA', name: 'Pennsylvania' }, { code: 'RI', name: 'Rhode Island' },
  { code: 'SC', name: 'South Carolina' }, { code: 'SD', name: 'South Dakota' }, { code: 'TN', name: 'Tennessee' },
  { code: 'TX', name: 'Texas' }, { code: 'UT', name: 'Utah' }, { code: 'VT', name: 'Vermont' },
  { code: 'VA', name: 'Virginia' }, { code: 'WA', name: 'Washington' }, { code: 'WV', name: 'West Virginia' },
  { code: 'WI', name: 'Wisconsin' }, { code: 'WY', name: 'Wyoming' }, { code: 'DC', name: 'Washington D.C.' }
];

function Checkout() {
  const navigate = useNavigate();
  const location = useLocation();
  const [processing, setProcessing] = useState(false);
  const [user, setUser] = useState(null);
  const [shippingCalculation, setShippingCalculation] = useState(null);
  const [calculatingShipping, setCalculatingShipping] = useState(false);

  // Address form state
  const [address, setAddress] = useState({
    street: '',
    city: '',
    state: '',
    zip_code: ''
  });
  const [saveAddress, setSaveAddress] = useState(true);
  const [useSavedAddress, setUseSavedAddress] = useState(false);
  
  // Guest checkout fields
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [emailStatus, setEmailStatus] = useState(null); // null, 'available', 'exists'
  const [checkingEmail, setCheckingEmail] = useState(false);
  
  // OTP Verification state
  const [otpSent, setOtpSent] = useState(false);
  const [otpCode, setOtpCode] = useState('');
  const [otpVerified, setOtpVerified] = useState(false);
  const [verificationToken, setVerificationToken] = useState('');
  const [sendingOtp, setSendingOtp] = useState(false);
  const [verifyingOtp, setVerifyingOtp] = useState(false);
  
  // Company info for invoice (optional)
  const [companyInfo, setCompanyInfo] = useState({
    company_name: '',
    address: '',
    phone: '',
    tax_id: ''
  });
  const [showCompanyInfo, setShowCompanyInfo] = useState(false);
  
  // Insurance option
  const [hasInsurance, setHasInsurance] = useState(false);
  
  // Tax state
  const [taxData, setTaxData] = useState(null);
  const [calculatingTax, setCalculatingTax] = useState(false);
  const [isTaxExempt, setIsTaxExempt] = useState(false);

  // Payment options
  const [paymentAmount, setPaymentAmount] = useState('deposit'); // 'deposit' or 'full'
  const [paymentMethod, setPaymentMethod] = useState('card'); // 'card', 'wire', 'check'
  
  // Terms acceptance
  const [termsAccepted, setTermsAccepted] = useState(false);

  const pledgeData = location.state?.pledgeData;
  
  // Auto-select wire for orders $15K+ (fraud prevention)
  useEffect(() => {
    if (pledgeData && parseFloat(pledgeData.total_price) >= 15000 && paymentMethod === 'card') {
      setPaymentMethod('wire');
    }
  }, [pledgeData, paymentMethod]);

  useEffect(() => {
    // Fetch user data (try localStorage token first, then cookie)
    const fetchUser = async () => {
      try {
        const response = await axios.get(`${BACKEND_URL}/api/me`, getAuthConfig());
        setUser(response.data);
        setEmail(response.data.email || '');
        
        // If user has saved address, populate form
        if (response.data.saved_address) {
          setAddress(response.data.saved_address);
          setUseSavedAddress(true);
        }
      } catch (error) {
        console.error('Not logged in - guest checkout mode');
        setUser(null);
      }
    };

    fetchUser();

    // Redirect if no pledge data
    if (!pledgeData) {
      navigate('/products');
    }
  }, [pledgeData, navigate]);

  // Calculate shipping when ZIP code is entered
  useEffect(() => {
    const calculateShipping = async () => {
      if (address.zip_code && address.zip_code.length === 5 && pledgeData) {
        setCalculatingShipping(true);
        setShippingCalculation(null);
        try {
          const response = await axios.post(`${BACKEND_URL}/api/shipping/calculate`, {
            quantity_sqft: pledgeData.sqft,
            zip_code: address.zip_code,
            material_type: pledgeData.product_name.toLowerCase().includes('hardwood') ? 'hardwood' : 'lvp'
          });
          setShippingCalculation(response.data);
        } catch (error) {
          console.error('Error calculating shipping:', error);
          toast.error('Could not calculate shipping costs. Please check your ZIP code.');
        } finally {
          setCalculatingShipping(false);
        }
      } else if (address.zip_code && address.zip_code.length < 5) {
        setShippingCalculation(null);
      }
    };

    const debounceTimer = setTimeout(calculateShipping, 800);
    return () => clearTimeout(debounceTimer);
  }, [address.zip_code, pledgeData]);

  // Calculate tax when state or payment amount changes
  useEffect(() => {
    const calculateTax = async () => {
      if (address.state && address.state.length === 2 && pledgeData) {
        setCalculatingTax(true);
        try {
          // Tax on 30% deposit or full amount based on selection
          const subtotal = paymentAmount === 'full' 
            ? parseFloat(pledgeData.total_price) 
            : parseFloat(pledgeData.total_price) * 0.30;
          const response = await axios.get(`${BACKEND_URL}/api/tax/calculate`, {
            params: {
              state: address.state.toUpperCase(),
              subtotal: subtotal
            }
          });
          setTaxData(response.data);
        } catch (error) {
          console.error('Error calculating tax:', error);
          setTaxData(null);
        } finally {
          setCalculatingTax(false);
        }
      } else {
        setTaxData(null);
      }
    };

    const debounceTimer = setTimeout(calculateTax, 500);
    return () => clearTimeout(debounceTimer);
  }, [address.state, pledgeData, paymentAmount]);

  // Check if email already has an account (only when not logged in)
  useEffect(() => {
    if (user || !email || !email.includes('@')) {
      setEmailStatus(null);
      return;
    }
    const timer = setTimeout(async () => {
      setCheckingEmail(true);
      try {
        const res = await axios.post(`${BACKEND_URL}/api/auth/check-email`, { email });
        setEmailStatus(res.data.exists ? 'exists' : 'available');
      } catch {
        setEmailStatus(null);
      } finally {
        setCheckingEmail(false);
      }
    }, 600);
    return () => clearTimeout(timer);
  }, [email, user]);

  if (!pledgeData) {
    return null;
  }

  // OTP Functions
  const sendOtp = async () => {
    if (!email || !email.includes('@')) {
      toast.error('Please enter a valid email address');
      return;
    }
    setSendingOtp(true);
    try {
      await axios.post(`${BACKEND_URL}/api/otp/send`, { email: email.toLowerCase().trim() });
      setOtpSent(true);
      toast.success('Verification code sent to your email');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send verification code');
    } finally {
      setSendingOtp(false);
    }
  };

  const verifyOtp = async () => {
    if (!otpCode || otpCode.length !== 6) {
      toast.error('Please enter the 6-digit code');
      return;
    }
    setVerifyingOtp(true);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/otp/verify`, { 
        email: email.toLowerCase().trim(), 
        otp_code: otpCode 
      });
      setOtpVerified(true);
      setVerificationToken(res.data.verification_token);
      toast.success('Email verified successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid verification code');
    } finally {
      setVerifyingOtp(false);
    }
  };

  // Calculate costs based on payment amount selection
  const totalProductCost = parseFloat(pledgeData.total_price);
  const isFullPayment = paymentAmount === 'full';
  const isCardPayment = paymentMethod === 'card';
  
  // Base amount (30% or 100%)
  const baseAmount = isFullPayment ? totalProductCost : totalProductCost * 0.30;
  // Tax is 0 if tax exempt
  const taxAmount = isTaxExempt ? 0 : (taxData?.tax_amount || 0);
  const subtotalWithTax = baseAmount + taxAmount;
  
  // Service fee only for card payments
  const serviceFee = isCardPayment ? subtotalWithTax * SERVICE_FEE_PERCENTAGE : 0;
  const totalDueNow = (subtotalWithTax + serviceFee).toFixed(2);
  const remainingBalance = isFullPayment ? 0 : (totalProductCost * 0.70).toFixed(2);

  const handlePayment = async () => {
    // Validate email
    if (!email || !email.includes('@')) {
      toast.error('Please enter a valid email address');
      return;
    }

    // Check OTP verification (required for all checkouts)
    if (!otpVerified) {
      toast.error('Please verify your email first');
      return;
    }

    // Validate account creation (if not logged in)
    if (!user && emailStatus !== 'exists') {
      if (!password || password.length < 6) {
        toast.error('Password must be at least 6 characters');
        return;
      }
      if (password !== confirmPassword) {
        toast.error('Passwords do not match');
        return;
      }
    }

    if (emailStatus === 'exists' && !user) {
      toast.error('This email already has an account. Please log in first.');
      return;
    }

    // Validate address
    if (!address.street || !address.city || !address.state || !address.zip_code) {
      toast.error('Please complete all address fields');
      return;
    }

    if (address.zip_code.length !== 5) {
      toast.error('Please enter a valid 5-digit ZIP code');
      return;
    }

    if (address.state.length !== 2) {
      toast.error('Please select a valid state');
      return;
    }

    setProcessing(true);

    try {
      // Calculate insurance cost if selected
      const totalOrderValue = totalProductCost + (shippingCalculation?.fcl?.total || 0);
      const insuranceCost = hasInsurance ? (totalOrderValue * 0.02) : 0;

      const orderData = {
        order_type: isFullPayment ? 'fcl_full' : 'fcl_deposit',
        product_cost: totalProductCost,
        shipping_cost: 0,
        insurance_cost: 0,
        tax_amount: taxAmount,
        tax_exempt: isTaxExempt,
        tax_rate: taxData?.tax_rate || 0,
        state: address.state.toUpperCase(),
        origin_url: window.location.origin,
        email: email,
        payment_method: paymentMethod,
        payment_amount_type: paymentAmount,
        verification_token: verificationToken,  // OTP verification token
        create_account_password: !user && emailStatus !== 'exists' ? password : undefined,
        pledge_data: {
          ...pledgeData,
          shipping_address: address,
          estimated_shipping_cost: shippingCalculation?.fcl?.total || 0,
          has_insurance: hasInsurance,
          insurance_cost: insuranceCost,
          company_info: companyInfo.company_name ? companyInfo : undefined
        }
      };

      if (paymentMethod === 'card') {
        // Create Stripe checkout session
        const response = await axios.post(`${BACKEND_URL}/api/payments/create-checkout`, orderData);

        // Redirect to Stripe checkout
        if (response.data.checkout_url) {
          window.location.href = response.data.checkout_url;
        } else {
          throw new Error('No checkout URL received');
        }
      } else {
        // For wire/check - create pending order
        const response = await axios.post(`${BACKEND_URL}/api/orders/create-pending`, orderData);

        // Navigate to confirmation page with pending order details
        navigate('/pledge/confirmation', { 
          state: { 
            orderData: response.data,
            paymentMethod: paymentMethod,
            isPending: true
          } 
        });
      }
    } catch (error) {
      setProcessing(false);
      console.error('Payment error:', error);
      toast.error(error.response?.data?.detail || 'Could not process your order. Please try again.');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-gray-50">
      <Navbar />
      
      <div className="pt-36 pb-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="mb-8">
            <button
              onClick={() => navigate('/products')}
              className="flex items-center text-gray-600 hover:text-primary mb-4"
              data-testid="back-to-products"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Products
            </button>
            <h1 className="font-bold text-4xl mb-2">Checkout</h1>
            <p className="text-gray-600">Pay 30% deposit to secure your order — 70% due upon production completion</p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Order Summary & Address */}
            <div className="lg:col-span-2 space-y-6">
              {/* Order Summary */}
              <div className="bg-white rounded-3xl shadow-xl p-8 border-2 border-gray-100">
                <h2 className="font-bold text-2xl mb-6">Order Summary</h2>
                
                <div className="space-y-4 mb-6">
                  <div className="flex justify-between py-3 border-b">
                    <span className="text-gray-600">Product</span>
                    <span className="font-semibold">{pledgeData.product_name}</span>
                  </div>
                  <div className="flex justify-between py-3 border-b">
                    <span className="text-gray-600">Quantity</span>
                    <span className="font-semibold">{parseInt(pledgeData.sqft).toLocaleString()} sqft</span>
                  </div>
                  <div className="flex justify-between py-3 border-b">
                    <span className="text-gray-600">Price per sqft</span>
                    <span className="font-semibold">${parseFloat(pledgeData.price_per_sqft).toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between py-3">
                    <span className="text-lg font-bold">Total Product Cost</span>
                    <span className="text-2xl font-bold text-primary">${totalProductCost.toFixed(2)}</span>
                  </div>
                </div>
              </div>

              {/* Shipping Address Form */}
              <div className="bg-white rounded-3xl shadow-xl p-8 border-2 border-gray-100">
                <div className="flex items-center mb-6">
                  <MapPin className="w-6 h-6 text-primary mr-3" />
                  <h2 className="font-bold text-2xl">Delivery Address</h2>
                </div>

                {useSavedAddress && user?.saved_address && (
                  <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-xl">
                    <p className="text-sm text-green-800 font-semibold mb-1">Using your saved address</p>
                    <button
                      onClick={() => {
                        setUseSavedAddress(false);
                        setAddress({ street: '', city: '', state: '', zip_code: '' });
                      }}
                      className="text-xs text-green-700 underline hover:text-green-900"
                    >
                      Use a different address
                    </button>
                  </div>
                )}

                <div className="space-y-4">
                  {/* Email field */}
                  <div>
                    <label className="block text-sm font-semibold mb-2">Email Address *</label>
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className={`w-full p-3 border-2 rounded-xl focus:outline-none ${
                        emailStatus === 'exists' ? 'border-amber-400 bg-amber-50' : 'border-gray-200 focus:border-primary'
                      }`}
                      placeholder="your.email@example.com"
                      required
                      disabled={!!user}
                      data-testid="email-input"
                    />
                    {!user && checkingEmail && <p className="text-xs text-gray-400 mt-1">Checking...</p>}
                    {!user && emailStatus === 'exists' && (
                      <div className="mt-2 p-3 bg-amber-50 border border-amber-200 rounded-xl">
                        <p className="text-sm font-semibold text-amber-800">This email already has an account.</p>
                        <button
                          onClick={() => navigate('/my-orders', { state: { returnTo: '/checkout' } })}
                          className="text-sm font-bold text-primary hover:underline mt-1"
                          data-testid="login-prompt-link"
                        >
                          Log in to continue checkout
                        </button>
                      </div>
                    )}
                    {user && <p className="text-xs text-green-600 mt-1 font-semibold">Logged in as {user.name || user.email}</p>}
                  </div>

                  {/* Password fields — only show for new users */}
                  {!user && emailStatus !== 'exists' && email.includes('@') && (
                    <div className="p-4 bg-blue-50 border border-blue-200 rounded-xl space-y-3">
                      <p className="text-sm font-semibold text-blue-800">Create your account to track orders & pay balances</p>
                      <div>
                        <label className="block text-sm font-semibold mb-1">Password *</label>
                        <input
                          type="password"
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          className="w-full p-3 border-2 border-gray-200 rounded-xl focus:border-primary focus:outline-none"
                          placeholder="Min 6 characters"
                          data-testid="password-input"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold mb-1">Confirm Password *</label>
                        <input
                          type="password"
                          value={confirmPassword}
                          onChange={(e) => setConfirmPassword(e.target.value)}
                          className={`w-full p-3 border-2 rounded-xl focus:outline-none ${
                            confirmPassword && password !== confirmPassword ? 'border-red-400' : 'border-gray-200 focus:border-primary'
                          }`}
                          placeholder="Re-enter password"
                          data-testid="confirm-password-input"
                        />
                        {confirmPassword && password !== confirmPassword && (
                          <p className="text-xs text-red-500 mt-1">Passwords don't match</p>
                        )}
                      </div>
                    </div>
                  )}

                  {/* OTP Verification Section - Show after email is entered */}
                  {email && email.includes('@') && emailStatus !== 'exists' && (
                    <div className={`p-4 rounded-xl border-2 ${otpVerified ? 'bg-green-50 border-green-300' : 'bg-gray-50 border-gray-200'}`}>
                      <div className="flex items-center gap-2 mb-3">
                        <ShieldCheck className={`w-5 h-5 ${otpVerified ? 'text-green-600' : 'text-gray-500'}`} />
                        <p className="text-sm font-semibold text-gray-800">
                          {otpVerified ? 'Email Verified ✓' : 'Verify Your Email'}
                        </p>
                      </div>
                      
                      {!otpVerified && (
                        <>
                          {!otpSent ? (
                            <div className="flex gap-2">
                              <Button
                                onClick={sendOtp}
                                disabled={sendingOtp}
                                className="bg-primary hover:bg-primary/90"
                                data-testid="send-otp-btn"
                              >
                                {sendingOtp ? (
                                  <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>Sending...</>
                                ) : (
                                  <><Send className="w-4 h-4 mr-2" />Send Verification Code</>
                                )}
                              </Button>
                            </div>
                          ) : (
                            <div className="space-y-3">
                              <p className="text-sm text-gray-600">Enter the 6-digit code sent to <strong>{email}</strong></p>
                              <div className="flex gap-2">
                                <input
                                  type="text"
                                  value={otpCode}
                                  onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                                  className="flex-1 p-3 border-2 border-gray-300 rounded-xl focus:border-primary focus:outline-none text-center text-2xl font-mono tracking-widest"
                                  placeholder="000000"
                                  maxLength={6}
                                  data-testid="otp-input"
                                />
                                <Button
                                  onClick={verifyOtp}
                                  disabled={verifyingOtp || otpCode.length !== 6}
                                  className="bg-green-600 hover:bg-green-700"
                                  data-testid="verify-otp-btn"
                                >
                                  {verifyingOtp ? 'Verifying...' : 'Verify'}
                                </Button>
                              </div>
                              <button
                                onClick={() => { setOtpSent(false); setOtpCode(''); }}
                                className="text-sm text-primary hover:underline"
                              >
                                Didn't receive code? Send again
                              </button>
                            </div>
                          )}
                        </>
                      )}
                      
                      {otpVerified && (
                        <p className="text-sm text-green-700">Your email has been verified. You can proceed with checkout.</p>
                      )}
                    </div>
                  )}

                  <div>
                    <label className="block text-sm font-semibold mb-2">Street Address *</label>
                    <input
                      type="text"
                      value={address.street}
                      onChange={(e) => setAddress({ ...address, street: e.target.value })}
                      className="w-full p-3 border-2 border-gray-200 rounded-xl focus:border-primary focus:outline-none"
                      placeholder="123 Main St"
                      required
                      data-testid="street-input"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-semibold mb-2">City *</label>
                      <input
                        type="text"
                        value={address.city}
                        onChange={(e) => setAddress({ ...address, city: e.target.value })}
                        className="w-full p-3 border-2 border-gray-200 rounded-xl focus:border-primary focus:outline-none"
                        placeholder="Des Moines"
                        required
                        data-testid="city-input"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold mb-2">State *</label>
                      <select
                        value={address.state}
                        onChange={(e) => setAddress({ ...address, state: e.target.value })}
                        className="w-full p-3 border-2 border-gray-200 rounded-xl focus:border-primary focus:outline-none bg-white"
                        required
                        data-testid="state-input"
                      >
                        <option value="">Select</option>
                        {US_STATES.map((s) => (
                          <option key={s.code} value={s.code}>{s.code}</option>
                        ))}
                      </select>
                      {taxData && (
                        <p className="text-xs text-gray-500 mt-1">
                          Sales tax: {taxData.tax_rate_display}
                          {taxData.tax_rate === 0 && <span className="text-green-600 font-semibold"> (No sales tax!)</span>}
                        </p>
                      )}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold mb-2">ZIP Code *</label>
                    <input
                      type="text"
                      value={address.zip_code}
                      onChange={(e) => setAddress({ ...address, zip_code: e.target.value.replace(/\D/g, '').slice(0, 5) })}
                      className="w-full p-3 border-2 border-gray-200 rounded-xl focus:border-primary focus:outline-none"
                      placeholder="50309"
                      maxLength="5"
                      required
                      data-testid="zip-input"
                    />
                  </div>
                </div>
              </div>

              {/* Company Info (Optional) for Invoice */}
              <div className="bg-white rounded-3xl shadow-xl p-8 border-2 border-gray-100">
                <button
                  onClick={() => setShowCompanyInfo(!showCompanyInfo)}
                  className="flex items-center justify-between w-full text-left"
                >
                  <div className="flex items-center">
                    <Building2 className="w-6 h-6 text-primary mr-3" />
                    <div>
                      <h2 className="font-bold text-xl">Company Information</h2>
                      <p className="text-sm text-gray-500">Optional - appears on your invoice</p>
                    </div>
                  </div>
                  <span className={`text-2xl transition-transform ${showCompanyInfo ? 'rotate-45' : ''}`}>+</span>
                </button>

                {showCompanyInfo && (
                  <div className="mt-6 space-y-4 pt-4 border-t">
                    <div>
                      <label className="block text-sm font-semibold mb-2">Company Name</label>
                      <input
                        type="text"
                        value={companyInfo.company_name}
                        onChange={(e) => setCompanyInfo({ ...companyInfo, company_name: e.target.value })}
                        className="w-full p-3 border-2 border-gray-200 rounded-xl focus:border-primary focus:outline-none"
                        placeholder="Acme Construction LLC"
                        data-testid="company-name-input"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold mb-2">Company Address</label>
                      <input
                        type="text"
                        value={companyInfo.address}
                        onChange={(e) => setCompanyInfo({ ...companyInfo, address: e.target.value })}
                        className="w-full p-3 border-2 border-gray-200 rounded-xl focus:border-primary focus:outline-none"
                        placeholder="123 Business Blvd, Suite 100, City, State ZIP"
                        data-testid="company-address-input"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-semibold mb-2">Phone</label>
                        <input
                          type="text"
                          value={companyInfo.phone}
                          onChange={(e) => setCompanyInfo({ ...companyInfo, phone: e.target.value })}
                          className="w-full p-3 border-2 border-gray-200 rounded-xl focus:border-primary focus:outline-none"
                          placeholder="(555) 123-4567"
                          data-testid="company-phone-input"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold mb-2">Tax ID / EIN</label>
                        <input
                          type="text"
                          value={companyInfo.tax_id}
                          onChange={(e) => setCompanyInfo({ ...companyInfo, tax_id: e.target.value })}
                          className="w-full p-3 border-2 border-gray-200 rounded-xl focus:border-primary focus:outline-none"
                          placeholder="12-3456789"
                          data-testid="company-taxid-input"
                        />
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Insurance Option */}
              <div className="bg-white rounded-3xl shadow-xl p-8 border-2 border-gray-100">
                <div className="flex items-center mb-4">
                  <Check className="w-6 h-6 text-primary mr-3" />
                  <h2 className="font-bold text-2xl">Shipping Insurance</h2>
                </div>

                <div className="p-4 bg-blue-50 rounded-xl mb-4">
                  <p className="text-sm text-gray-700 mb-2">
                    Protect your investment with shipping insurance
                  </p>
                  <ul className="text-xs text-gray-600 space-y-1 ml-4">
                    <li>• Covers loss or damage during transit</li>
                    <li>• Only 2% of total order value (product + shipping)</li>
                    <li>• Full replacement or refund if anything happens</li>
                  </ul>
                </div>

                <div className="flex items-start p-4 border-2 border-gray-200 rounded-xl hover:border-primary transition-colors">
                  <input
                    type="checkbox"
                    id="hasInsurance"
                    checked={hasInsurance}
                    onChange={(e) => setHasInsurance(e.target.checked)}
                    className="w-5 h-5 text-primary border-gray-300 rounded focus:ring-primary mt-0.5"
                    data-testid="insurance-checkbox"
                  />
                  <label htmlFor="hasInsurance" className="ml-3 flex-1 cursor-pointer">
                    <p className="font-semibold text-gray-900">Yes, add shipping insurance</p>
                    <p className="text-sm text-gray-600 mt-1">
                      {shippingCalculation ? (
                        <>
                          Estimated: <span className="font-semibold text-primary">
                            ${((totalProductCost + shippingCalculation.fcl.total) * 0.02).toFixed(2)}
                          </span> (paid when container ships)
                        </>
                      ) : (
                        'Cost calculated after shipping estimate'
                      )}
                    </p>
                  </label>
                </div>
              </div>

              {/* Shipping Cost Calculator */}
              {shippingCalculation && (
                <div className="bg-white rounded-3xl shadow-xl p-8 border-2 border-gray-100">
                  <div className="flex items-center mb-6">
                    <Truck className="w-6 h-6 text-primary mr-3" />
                    <h2 className="font-bold text-2xl">Shipping Options</h2>
                  </div>

                  <div className="mb-4 p-4 bg-blue-50 rounded-xl">
                    <p className="text-sm font-semibold text-gray-700 mb-1">Nearest Port</p>
                    <p className="text-lg font-bold text-primary">{shippingCalculation.port.name}</p>
                    <p className="text-xs text-gray-600">~{shippingCalculation.port.estimated_distance_miles} miles to your location</p>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    {/* FCL Option - Recommended */}
                    <div className="relative p-5 border-2 border-primary bg-primary/5 rounded-2xl">
                      <div className="absolute top-2 right-2">
                        <span className="px-2 py-1 bg-primary text-white text-xs font-bold rounded-full">BEST VALUE</span>
                      </div>
                      <div className="mb-3">
                        <Package className="w-6 h-6 text-primary mb-2" />
                        <h3 className="font-bold text-lg">{shippingCalculation.fcl.name}</h3>
                        <p className="text-xs text-gray-600 mt-1">{shippingCalculation.fcl.description}</p>
                      </div>
                      <div className="space-y-2 text-sm mb-3">
                        <div className="flex justify-between pt-2 border-t-2 border-primary/20">
                          <span className="font-bold">Estimated Shipping:</span>
                          <span className="font-bold text-primary text-lg">${shippingCalculation.fcl.total}</span>
                        </div>
                        <div className="text-xs text-gray-600">
                          ${shippingCalculation.fcl.per_sqft}/sqft • ~{shippingCalculation.fcl.delivery_days} days
                        </div>
                      </div>
                    </div>

                    {/* LCL Option */}
                    <div className="p-5 border-2 border-gray-200 rounded-2xl hover:border-gray-300 transition-colors">
                      <div className="mb-3">
                        <Truck className="w-6 h-6 text-gray-600 mb-2" />
                        <h3 className="font-bold text-lg">{shippingCalculation.lcl.name}</h3>
                        <p className="text-xs text-gray-600 mt-1">{shippingCalculation.lcl.description}</p>
                      </div>
                      <div className="space-y-2 text-sm mb-3">
                        <div className="flex justify-between pt-2 border-t-2 border-gray-200">
                          <span className="font-bold">Total:</span>
                          <span className="font-bold text-gray-700 text-lg">${shippingCalculation.lcl.total}</span>
                        </div>
                        <div className="text-xs text-gray-600">
                          ${shippingCalculation.lcl.per_sqft}/sqft • ~{shippingCalculation.lcl.delivery_days} days
                        </div>
                      </div>
                      <button
                        onClick={() => navigate('/ship-now', { state: { pledgeData, shippingCalculation, email, address } })}
                        className="w-full mt-2 py-2 px-4 bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm font-semibold rounded-xl transition-colors"
                        data-testid="ship-now-btn"
                      >
                        Ship Now (Pay Full)
                      </button>
                    </div>
                  </div>

                  {/* Savings Banner */}
                  <div className="p-6 bg-gradient-to-r from-green-500 to-green-600 rounded-2xl shadow-lg">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-2xl font-bold text-white mb-1">
                          💰 Save ${shippingCalculation.savings.amount}
                        </p>
                        <p className="text-base text-green-50 font-semibold">
                          That's {shippingCalculation.savings.percentage}% off with Shared Container
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {calculatingShipping && (
                <div className="bg-white rounded-3xl shadow-xl p-8 border-2 border-primary/30 text-center animate-pulse">
                  <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                  <p className="text-lg font-semibold text-primary mb-1">Calculating shipping costs...</p>
                  <p className="text-sm text-gray-600">Finding nearest port for ZIP {address.zip_code}</p>
                </div>
              )}

              {/* Payment Options */}
              <div className="bg-white rounded-3xl shadow-xl p-8 border-2 border-gray-100">
                <h2 className="font-bold text-2xl mb-6">Payment Options</h2>
                
                {/* Payment Amount Selection */}
                <div className="mb-8">
                  <h3 className="font-semibold text-lg mb-4">How much would you like to pay?</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* 30% Deposit Option */}
                    <div
                      onClick={() => setPaymentAmount('deposit')}
                      className={`p-5 rounded-2xl border-2 cursor-pointer transition-all ${
                        paymentAmount === 'deposit'
                          ? 'border-primary bg-primary/5'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                      data-testid="payment-deposit-option"
                    >
                      <div className="flex items-start gap-3">
                        <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center mt-0.5 ${
                          paymentAmount === 'deposit' ? 'border-primary' : 'border-gray-300'
                        }`}>
                          {paymentAmount === 'deposit' && <div className="w-2.5 h-2.5 rounded-full bg-primary" />}
                        </div>
                        <div className="flex-1">
                          <p className="font-bold text-lg">30% Deposit</p>
                          <p className="text-2xl font-bold text-primary mt-1">
                            ${(totalProductCost * 0.30).toFixed(2)}
                          </p>
                          <p className="text-sm text-gray-600 mt-2">
                            Pay now, then ${(totalProductCost * 0.70).toFixed(2)} when ready to ship
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Full Payment Option */}
                    <div
                      onClick={() => setPaymentAmount('full')}
                      className={`p-5 rounded-2xl border-2 cursor-pointer transition-all relative ${
                        paymentAmount === 'full'
                          ? 'border-primary bg-primary/5'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                      data-testid="payment-full-option"
                    >
                      <div className="flex items-start gap-3">
                        <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center mt-0.5 ${
                          paymentAmount === 'full' ? 'border-primary' : 'border-gray-300'
                        }`}>
                          {paymentAmount === 'full' && <div className="w-2.5 h-2.5 rounded-full bg-primary" />}
                        </div>
                        <div className="flex-1">
                          <p className="font-bold text-lg">Pay in Full</p>
                          <p className="text-2xl font-bold text-primary mt-1">
                            ${totalProductCost.toFixed(2)}
                          </p>
                          <p className="text-sm text-gray-600 mt-2">
                            One-time payment, no balance due later
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Payment Method Selection */}
                <div className="mb-6">
                  <h3 className="font-semibold text-lg mb-4">How would you like to pay?</h3>
                  
                  {/* Large Order Notice - Wire/Check Required for $15K+ */}
                  {totalProductCost >= 15000 && (
                    <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl mb-4">
                      <div className="flex items-start gap-3">
                        <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                        <div>
                          <p className="font-semibold text-amber-900">Large Order Payment Policy</p>
                          <p className="text-sm text-amber-800 mt-1">
                            Orders over $15,000 require payment via <strong>Wire Transfer</strong> or <strong>Cashier's Check</strong> for added security. 
                            This protects both you and BlueCrate from payment fraud.
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  <div className="space-y-3">
                    {/* Credit Card - Disabled for orders $15K+ */}
                    <div
                      onClick={() => totalProductCost < 15000 && setPaymentMethod('card')}
                      className={`p-4 rounded-xl border-2 transition-all ${
                        totalProductCost >= 15000 
                          ? 'border-gray-200 bg-gray-50 opacity-60 cursor-not-allowed'
                          : paymentMethod === 'card'
                            ? 'border-primary bg-primary/5 cursor-pointer'
                            : 'border-gray-200 hover:border-gray-300 cursor-pointer'
                      }`}
                      data-testid="payment-method-card"
                    >
                      <div className="flex items-center gap-4">
                        <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                          totalProductCost >= 15000 ? 'border-gray-300' :
                          paymentMethod === 'card' ? 'border-primary' : 'border-gray-300'
                        }`}>
                          {paymentMethod === 'card' && totalProductCost < 15000 && <div className="w-2.5 h-2.5 rounded-full bg-primary" />}
                        </div>
                        <CreditCard className={`w-6 h-6 ${
                          totalProductCost >= 15000 ? 'text-gray-400' :
                          paymentMethod === 'card' ? 'text-primary' : 'text-gray-400'
                        }`} />
                        <div className="flex-1">
                          <p className={`font-semibold ${totalProductCost >= 15000 ? 'text-gray-500' : ''}`}>Credit / Debit Card</p>
                          <p className="text-sm text-gray-600">
                            {totalProductCost >= 15000 
                              ? 'Not available for orders over $15,000' 
                              : 'Secure payment via Square • 3% service fee'}
                          </p>
                        </div>
                        {totalProductCost >= 15000 && (
                          <span className="px-2 py-1 bg-gray-200 text-gray-600 text-xs font-medium rounded-full">Unavailable</span>
                        )}
                      </div>
                    </div>

                    {/* Wire Transfer */}
                    <div
                      onClick={() => setPaymentMethod('wire')}
                      className={`p-4 rounded-xl border-2 cursor-pointer transition-all ${
                        paymentMethod === 'wire'
                          ? 'border-primary bg-primary/5'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                      data-testid="payment-method-wire"
                    >
                      <div className="flex items-center gap-4">
                        <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                          paymentMethod === 'wire' ? 'border-primary' : 'border-gray-300'
                        }`}>
                          {paymentMethod === 'wire' && <div className="w-2.5 h-2.5 rounded-full bg-primary" />}
                        </div>
                        <Building2 className={`w-6 h-6 ${paymentMethod === 'wire' ? 'text-primary' : 'text-gray-400'}`} />
                        <div className="flex-1">
                          <p className="font-semibold">Wire Transfer</p>
                          <p className="text-sm text-gray-600">Bank details sent via email • No service fee</p>
                        </div>
                        {(paymentMethod === 'wire' || totalProductCost >= 15000) && (
                          <span className="px-2 py-1 bg-green-100 text-green-700 text-xs font-bold rounded-full">
                            {totalProductCost >= 15000 ? 'Recommended' : 'Save 3%'}
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Check by Mail */}
                    <div
                      onClick={() => setPaymentMethod('check')}
                      className={`p-4 rounded-xl border-2 cursor-pointer transition-all ${
                        paymentMethod === 'check'
                          ? 'border-primary bg-primary/5'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                      data-testid="payment-method-check"
                    >
                      <div className="flex items-center gap-4">
                        <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                          paymentMethod === 'check' ? 'border-primary' : 'border-gray-300'
                        }`}>
                          {paymentMethod === 'check' && <div className="w-2.5 h-2.5 rounded-full bg-primary" />}
                        </div>
                        <Mail className={`w-6 h-6 ${paymentMethod === 'check' ? 'text-primary' : 'text-gray-400'}`} />
                        <div className="flex-1">
                          <p className="font-semibold">Mail a Check</p>
                          <p className="text-sm text-gray-600">Cashier's / Official Check only • No service fee</p>
                        </div>
                        {paymentMethod === 'check' && (
                          <span className="px-2 py-1 bg-green-100 text-green-700 text-xs font-bold rounded-full">Save 3%</span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Wire Transfer Instructions */}
                {paymentMethod === 'wire' && (
                  <div className="p-5 bg-blue-50 rounded-2xl border border-blue-200 mb-6">
                    <div className="flex items-start gap-3">
                      <Building2 className="w-6 h-6 text-blue-600 mt-0.5" />
                      <div>
                        <p className="font-bold text-blue-900 mb-2">Wire Transfer Instructions</p>
                        <p className="text-sm text-blue-800 mb-3">
                          After placing your order, we will send bank account details to your email address for secure wire transfer.
                        </p>
                        <div className="flex items-center gap-2 p-3 bg-amber-50 rounded-xl border border-amber-200">
                          <Clock className="w-5 h-5 text-amber-600" />
                          <p className="text-sm text-amber-800">
                            <span className="font-semibold">72-hour hold:</span> Your container space will be reserved for 72 hours while we await payment.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Check by Mail Instructions */}
                {paymentMethod === 'check' && (
                  <div className="p-5 bg-blue-50 rounded-2xl border border-blue-200 mb-6">
                    <div className="flex items-start gap-3">
                      <Mail className="w-6 h-6 text-blue-600 mt-0.5" />
                      <div className="flex-1">
                        <p className="font-bold text-blue-900 mb-2">Check Mailing Instructions</p>
                        
                        {/* Important Warning */}
                        <div className="flex items-start gap-2 p-3 bg-red-50 rounded-xl border border-red-200 mb-4">
                          <AlertTriangle className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
                          <div>
                            <p className="text-sm font-bold text-red-800">IMPORTANT: Cashier's / Official Bank Checks ONLY</p>
                            <p className="text-xs text-red-700 mt-1">Personal checks will NOT be accepted and will be returned.</p>
                          </div>
                        </div>

                        <p className="text-sm text-blue-800 mb-2">Mail your cashier's check to:</p>
                        <div className="p-3 bg-white rounded-xl border border-blue-200">
                          <p className="font-semibold text-gray-900">BlueCrate Group</p>
                          <p className="text-gray-700">8 The Green STE B</p>
                          <p className="text-gray-700">Dover, DE 19901</p>
                        </div>

                        <div className="flex items-center gap-2 p-3 bg-amber-50 rounded-xl border border-amber-200 mt-4">
                          <Clock className="w-5 h-5 text-amber-600" />
                          <p className="text-sm text-amber-800">
                            <span className="font-semibold">72-hour hold:</span> Your container space will be reserved for 72 hours while we await payment.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Payment Summary */}
              <div className="bg-white rounded-3xl shadow-xl p-8 border-2 border-gray-100">
                <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-2xl p-6">
                  <h3 className="font-bold text-lg mb-4">Payment Summary</h3>
                  <div className="space-y-3">
                    <div className="flex justify-between items-center p-3 bg-white rounded-xl">
                      <div>
                        <p className="font-semibold">{isFullPayment ? '100% Full Payment' : '30% Deposit'}</p>
                        <p className="text-xs text-gray-600">{isFullPayment ? 'Complete payment' : 'Secures your order'}</p>
                      </div>
                      <span className="text-xl font-bold text-gray-700">${baseAmount.toFixed(2)}</span>
                    </div>
                    
                    {/* Tax Exempt Checkbox */}
                    {taxData?.tax_rate > 0 && (
                      <div 
                        onClick={() => setIsTaxExempt(!isTaxExempt)}
                        className={`flex items-center gap-3 p-3 rounded-xl cursor-pointer transition-all ${
                          isTaxExempt ? 'bg-green-50 border-2 border-green-400' : 'bg-white border border-gray-200 hover:border-gray-300'
                        }`}
                        data-testid="tax-exempt-checkbox"
                      >
                        <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                          isTaxExempt ? 'bg-green-500 border-green-500' : 'border-gray-300'
                        }`}>
                          {isTaxExempt && <Check className="w-3 h-3 text-white" />}
                        </div>
                        <div className="flex-1">
                          <p className="font-semibold text-sm">Tax Exempt</p>
                          <p className="text-xs text-gray-500">Documentation required within 72 hours</p>
                        </div>
                      </div>
                    )}
                    
                    {/* Tax Display */}
                    {isTaxExempt ? (
                      <div className="flex justify-between items-center p-3 bg-green-50 rounded-xl border border-green-200">
                        <div>
                          <p className="font-semibold text-green-700">Tax Exempt Applied</p>
                          <p className="text-xs text-green-600">Upload documentation within 72 hours</p>
                        </div>
                        <span className="text-lg font-semibold text-green-600">$0.00</span>
                      </div>
                    ) : taxAmount > 0 ? (
                      <div className="flex justify-between items-center p-3 bg-white rounded-xl">
                        <div className="flex items-center">
                          <p className="font-semibold">Sales Tax ({taxData?.tax_rate_display || '0%'})</p>
                          <div className="relative ml-2 group">
                            <Info className="w-4 h-4 text-gray-400 cursor-help" />
                            <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-10">
                              {address.state} state sales tax on building materials
                            </div>
                          </div>
                        </div>
                        <span className="text-lg font-semibold text-gray-600">${taxAmount.toFixed(2)}</span>
                      </div>
                    ) : taxData?.tax_rate === 0 && address.state ? (
                      <div className="flex justify-between items-center p-3 bg-green-50 rounded-xl border border-green-200">
                        <div>
                          <p className="font-semibold text-green-700">No Sales Tax!</p>
                          <p className="text-xs text-green-600">{address.state} has no state sales tax</p>
                        </div>
                        <span className="text-lg font-semibold text-green-600">$0.00</span>
                      </div>
                    ) : null}
                    
                    {isCardPayment ? (
                      <div className="flex justify-between items-center p-3 bg-white rounded-xl">
                        <div className="flex items-center">
                          <p className="font-semibold">Service Fee (3%)</p>
                          <div className="relative ml-2 group">
                            <Info className="w-4 h-4 text-gray-400 cursor-help" />
                            <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-10">
                              Covers credit card processing charges
                            </div>
                          </div>
                        </div>
                        <span className="text-lg font-semibold text-gray-600">${serviceFee.toFixed(2)}</span>
                      </div>
                    ) : (
                      <div className="flex justify-between items-center p-3 bg-green-50 rounded-xl border border-green-200">
                        <div>
                          <p className="font-semibold text-green-700">No Service Fee!</p>
                          <p className="text-xs text-green-600">{paymentMethod === 'wire' ? 'Wire transfer' : 'Check payment'} - save 3%</p>
                        </div>
                        <span className="text-lg font-semibold text-green-600">$0.00</span>
                      </div>
                    )}
                    <div className="flex justify-between items-center p-3 bg-primary/10 rounded-xl border-2 border-primary/20">
                      <div>
                        <p className="font-bold text-primary">Total Due {paymentMethod === 'card' ? 'Today' : 'Now'}</p>
                      </div>
                      <span className="text-2xl font-bold text-primary">${totalDueNow}</span>
                    </div>
                    {!isFullPayment && (
                      <div className="flex justify-between items-center p-3 bg-white rounded-xl">
                        <div>
                          <p className="font-semibold">70% Balance</p>
                          <p className="text-xs text-gray-600">Due upon production completion (+ applicable tax)</p>
                        </div>
                        <span className="text-xl font-bold text-gray-700">${remainingBalance}</span>
                      </div>
                    )}
                    {shippingCalculation && (
                      <div className="flex justify-between items-center p-3 bg-white rounded-xl">
                        <div>
                          <p className="font-semibold">Estimated Shipping</p>
                          <p className="text-xs text-gray-600">Shared container (FCL)</p>
                        </div>
                        <span className="text-xl font-bold text-gray-700">${shippingCalculation.fcl.total}</span>
                      </div>
                    )}
                  </div>
                  <div className="mt-4 p-3 bg-green-50 rounded-xl border border-green-200">
                    <p className="text-sm text-green-800">
                      <span className="font-semibold">Production starts</span> when container reaches 80% capacity or you can choose "Ship Now" for immediate production.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Payment Card */}
            <div className="lg:col-span-1">
              <div className="bg-white rounded-3xl shadow-xl p-8 border-2 border-gray-100 sticky top-28">
                <div className="text-center mb-6">
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-primary/10 rounded-full mb-4">
                    {paymentMethod === 'card' && <CreditCard className="w-8 h-8 text-primary" />}
                    {paymentMethod === 'wire' && <Building2 className="w-8 h-8 text-primary" />}
                    {paymentMethod === 'check' && <Mail className="w-8 h-8 text-primary" />}
                  </div>
                  <h3 className="font-bold text-xl mb-2">
                    {paymentMethod === 'card' && 'Secure Payment'}
                    {paymentMethod === 'wire' && 'Wire Transfer'}
                    {paymentMethod === 'check' && 'Mail a Check'}
                  </h3>
                  <p className="text-sm text-gray-600">
                    {paymentMethod === 'card' && 'Powered by Square'}
                    {paymentMethod === 'wire' && 'Bank details sent via email'}
                    {paymentMethod === 'check' && 'Cashier\'s check only'}
                  </p>
                </div>

                {/* Amount Breakdown */}
                <div className="mb-6 p-4 bg-primary/5 rounded-xl">
                  <div className="space-y-2 text-sm mb-3">
                    <div className="flex justify-between">
                      <span>{isFullPayment ? 'Full Payment:' : '30% Deposit:'}</span>
                      <span className="font-semibold">${baseAmount.toFixed(2)}</span>
                    </div>
                    {taxAmount > 0 && (
                      <div className="flex justify-between text-gray-600">
                        <span>Sales Tax ({taxData?.tax_rate_display}):</span>
                        <span>${taxAmount.toFixed(2)}</span>
                      </div>
                    )}
                    {isCardPayment ? (
                      <div className="flex justify-between text-gray-600">
                        <span>Service Fee (3%):</span>
                        <span>${serviceFee.toFixed(2)}</span>
                      </div>
                    ) : (
                      <div className="flex justify-between text-green-600">
                        <span>Service Fee:</span>
                        <span className="font-semibold">$0.00 (waived)</span>
                      </div>
                    )}
                    <div className="flex justify-between pt-2 border-t border-primary/20">
                      <span className="font-bold">Total:</span>
                      <span className="font-bold text-primary text-xl">${totalDueNow}</span>
                    </div>
                    {!isFullPayment && (
                      <div className="flex justify-between text-gray-500 text-xs pt-1">
                        <span>Balance due later:</span>
                        <span>${remainingBalance}</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* 72-hour hold warning for wire/check */}
                {(paymentMethod === 'wire' || paymentMethod === 'check') && (
                  <div className="mb-4 p-3 bg-amber-50 rounded-xl border border-amber-200">
                    <div className="flex items-center gap-2">
                      <Clock className="w-4 h-4 text-amber-600" />
                      <p className="text-xs text-amber-800">
                        <span className="font-semibold">72-hour hold</span> on container space
                      </p>
                    </div>
                  </div>
                )}

                {/* Square Card Payment Form */}
                {paymentMethod === 'card' && otpVerified && (
                  <div className="mb-4">
                    <SquarePaymentForm
                      amount={parseFloat(totalDueNow.replace(/,/g, ''))}
                      email={email}
                      verificationToken={verificationToken}
                      orderData={{
                        pledge_id: `pledge_${Date.now()}`,
                        product_name: pledgeData?.product_name,
                        sqft: pledgeData?.sqft,
                        order_type: isFullPayment ? 'fcl_full' : 'fcl_deposit',
                        shipping_address: address
                      }}
                      onSuccess={async (paymentData) => {
                        toast.success('Payment successful!');
                        // Create the pledge/order after successful payment
                        try {
                          const orderData = {
                            order_type: isFullPayment ? 'fcl_full' : 'fcl_deposit',
                            product_cost: totalProductCost,
                            shipping_cost: 0,
                            insurance_cost: 0,
                            tax_amount: taxAmount,
                            tax_exempt: isTaxExempt,
                            tax_rate: taxData?.tax_rate || 0,
                            state: address.state.toUpperCase(),
                            origin_url: window.location.origin,
                            email: email,
                            payment_method: 'card',
                            payment_amount_type: paymentAmount,
                            verification_token: verificationToken,
                            square_payment_id: paymentData.payment_id,
                            create_account_password: !user && emailStatus !== 'exists' ? password : undefined,
                            pledge_data: {
                              ...pledgeData,
                              shipping_address: address,
                              has_insurance: hasInsurance,
                              insurance_cost: hasInsurance ? ((parseFloat(pledgeData?.total_price || 0)) * 0.02) : 0,
                              company_info: companyInfo.company_name ? companyInfo : undefined
                            }
                          };
                          await axios.post(`${BACKEND_URL}/api/orders/create-pending`, orderData);
                          navigate('/order-success', { 
                            state: { 
                              orderType: 'card', 
                              email, 
                              amount: totalDueNow,
                              receiptUrl: paymentData.receipt_url 
                            } 
                          });
                        } catch (err) {
                          console.error('Order creation error:', err);
                          toast.error('Payment succeeded but order creation failed. Please contact support.');
                        }
                      }}
                      onError={(error) => {
                        toast.error(error || 'Payment failed');
                      }}
                      disabled={!otpVerified || !address.state || !termsAccepted}
                    />
                  </div>
                )}

                {/* Card payment - show OTP required message */}
                {paymentMethod === 'card' && !otpVerified && (
                  <div className="mb-4 p-4 bg-amber-50 rounded-xl border border-amber-200">
                    <div className="flex items-center gap-2">
                      <ShieldCheck className="w-5 h-5 text-amber-600" />
                      <p className="text-sm text-amber-800">
                        <span className="font-semibold">Verify your email</span> above to enable card payment
                      </p>
                    </div>
                  </div>
                )}

                {/* Terms and Conditions Checkbox */}
                <div className="mb-4 p-4 bg-gray-50 rounded-xl border border-gray-200">
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={termsAccepted}
                      onChange={(e) => setTermsAccepted(e.target.checked)}
                      className="w-5 h-5 mt-0.5 rounded border-gray-300 text-primary focus:ring-primary cursor-pointer"
                      data-testid="terms-checkbox"
                    />
                    <span className="text-sm text-gray-700 leading-relaxed">
                      I have read and agree to the{' '}
                      <Link to="/terms" target="_blank" className="text-primary font-semibold hover:underline">
                        Terms of Service
                      </Link>{' '}
                      and{' '}
                      <Link to="/payment-policy" target="_blank" className="text-primary font-semibold hover:underline">
                        Payment Policy
                      </Link>
                      , including the <span className="font-semibold">non-refundable deposit policy</span>, 
                      <span className="font-semibold"> 3% monthly late fee</span> on overdue balances, 
                      and <span className="font-semibold">chargeback terms</span>.
                    </span>
                  </label>
                </div>

                {/* Wire/Check - Pay/Place Order Button */}
                {(paymentMethod === 'wire' || paymentMethod === 'check') && (
                  <Button
                    onClick={handlePayment}
                    disabled={processing || !email || !address.state || !otpVerified || !termsAccepted}
                    className="w-full h-14 text-lg rounded-full bg-primary hover:bg-primary/90"
                    data-testid="pay-button"
                  >
                    {processing ? (
                      <>
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                        Placing Order...
                      </>
                    ) : (
                      <>
                        <Check className="w-5 h-5 mr-2" />
                        Place Order (${totalDueNow})
                      </>
                    )}
                  </Button>
                )}

                {/* Security Info */}
                <div className="mt-6 space-y-2">
                  {paymentMethod === 'card' ? (
                    <>
                      <div className="flex items-center text-xs text-gray-600">
                        <Check className="w-4 h-4 text-green-600 mr-2" />
                        Secure SSL encryption
                      </div>
                      <div className="flex items-center text-xs text-gray-600">
                        <Check className="w-4 h-4 text-green-600 mr-2" />
                        PCI DSS compliant
                      </div>
                      <div className="flex items-center text-xs text-gray-600">
                        <Check className="w-4 h-4 text-green-600 mr-2" />
                        3D Secure enabled
                      </div>
                      <div className="flex items-center text-xs text-gray-600">
                        <Check className="w-4 h-4 text-green-600 mr-2" />
                        Powered by Square
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="flex items-center text-xs text-gray-600">
                        <Check className="w-4 h-4 text-green-600 mr-2" />
                        No credit card processing fee
                      </div>
                      <div className="flex items-center text-xs text-gray-600">
                        <Check className="w-4 h-4 text-green-600 mr-2" />
                        Confirmation email sent instantly
                      </div>
                      <div className="flex items-center text-xs text-gray-600">
                        <Check className="w-4 h-4 text-green-600 mr-2" />
                        {paymentMethod === 'wire' ? 'Bank details in email' : 'Mailing address provided'}
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Checkout;
